import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { dictionaryQueryOptions } from '@/features/courses/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { DictionaryQuery } from '@/schemas/courses'
import {
  DEFAULT_PAGE_SIZE,
  pageParamSchema,
  pageSizeParamSchema,
  queryTextParamSchema,
} from '@/screens/courses/search'

/**
 * - `q` — the search, trimmed; absent is every term.
 * - `page`/`pageSize` — mm-be pages the list, so each page is a request.
 */
export const dictionarySearchSchema = z.object({
  q: queryTextParamSchema,
  page: pageParamSchema,
  pageSize: pageSizeParamSchema,
})
export type DictionarySearch = z.infer<typeof dictionarySearchSchema>

export const DICTIONARY_SEARCH_DEFAULTS = { page: 1, pageSize: DEFAULT_PAGE_SIZE } as const

/**
 * The dictionary read for a URL, in the normal form its key is built from
 * (`q` null when absent). The loader and the screen both go through this, so
 * a reload never keys the same search two ways.
 */
export const dictionaryQuery = (search: DictionarySearch): DictionaryQuery => ({
  q: search.q ?? null,
  page: search.page,
  pageSize: search.pageSize,
})

/**
 * The `/learn/dictionary` loader.
 *
 * MAIN: the entry URL's results. Keyed on `q` and the page, so the screen
 * reads it with `useDictionary` (`keepPreviousData`), not suspense — typing
 * dims the old results instead of suspending the route.
 */
export async function loadDictionary({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const search = dictionarySearchSchema.parse(location.search)
  await ensureRouteData(queryClient, dictionaryQueryOptions(dictionaryQuery(search)))
}
